import React, { useState, useEffect, memo } from 'react';
import { useUser } from '../contexts/UserContext';
import { logger } from '../utils/logger';

const InvitationAcceptance = memo(({ token, onSuccess, onError }) => {
  const { user, userProfile, acceptParentInvitation, getInvitationByToken, error } = useUser();
  const [invitation, setInvitation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (token) {
      validateInvitation();
    }
  }, [token]);

  const validateInvitation = async () => {
    setIsLoading(true);
    try {
      const result = await getInvitationByToken(token);
      if (result.success) {
        setInvitation(result.invitation);
      } else {
        if (onError) {
          onError(result.error || 'Invalid invitation');
        }
      }
    } catch (error) {
      logger.error('Error validating invitation:', error);
      if (onError) {
        onError('Failed to validate invitation');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user?.id) {
      if (onError) {
        onError('You must be logged in to accept an invitation. Please sign in first.');
      }
      return;
    }

    setIsAccepting(true);
    try {
      const result = await acceptParentInvitation(token);
      if (result.success) {
        setAccepted(true);
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        if (onError) {
          onError(result.error || 'Failed to accept invitation');
        }
      }
    } catch (error) {
      logger.error('Error accepting invitation:', error);
      if (onError) {
        onError(error.message || 'Failed to accept invitation');
      }
    } finally {
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Validating Invitation</h2>
          <p className="text-gray-600">Please wait...</p>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">
            This invitation link is invalid, expired, or has already been used.
          </p>
          <a
            href="/#/"
            className="inline-block bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Go to Home
          </a>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="text-6xl mb-4 animate-bounce">🎉</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Invitation Accepted!</h2>
          <p className="text-gray-600 mb-6">
            You're now connected to your parent's account. They can now see your learning progress!
          </p>
          <a
            href="/#/"
            className="inline-block bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Start Learning! 🚀
          </a>
        </div>
      </div>
    );
  }

  const isExpired = new Date(invitation.expires_at) < new Date();
  const parentEmail = invitation.parent_email;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">👨‍👩‍👧‍👦</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Parent Invitation</h2>
          <p className="text-gray-600">
            Connect your account to your parent's dashboard
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
            <div className="flex items-center">
              <span className="text-xl mr-2">⚠️</span>
              <span>{error}</span>
            </div>
          </div>
        )}

        {isExpired ? (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-lg mb-6">
            <div className="flex items-center">
              <span className="text-xl mr-2">⏰</span>
              <span>This invitation has expired. Please ask your parent for a new invitation.</span>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">Invitation Details</h3>
              <div className="text-sm text-blue-800 space-y-1">
                <p>
                  <span className="font-medium">From:</span> {parentEmail}
                </p>
                <p>
                  <span className="font-medium">Expires:</span>{' '}
                  {new Date(invitation.expires_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>

            {!user?.id ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <span className="text-xl mr-2">🔐</span>
                  <div>
                    <p className="font-semibold text-yellow-900 mb-1">Sign In Required</p>
                    <p className="text-sm text-yellow-800 mb-3">
                      You need to be logged in to accept this invitation.
                    </p>
                    <a
                      href="/#/?showLogin=true"
                      className="inline-block bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded text-sm font-medium"
                    >
                      Sign In / Sign Up
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    <span className="font-medium">Logged in as:</span> {userProfile?.display_name || userProfile?.email}
                  </p>
                </div>

                <button
                  onClick={handleAccept}
                  disabled={isAccepting}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isAccepting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Accepting...</span>
                    </>
                  ) : (
                    <>
                      <span>✓</span>
                      <span>Accept Invitation</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}

        <div className="mt-6 text-center">
          <a
            href="/#/"
            className="text-gray-600 hover:text-gray-800 text-sm"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
});

InvitationAcceptance.displayName = 'InvitationAcceptance';

export default InvitationAcceptance;

